import { useParams } from 'react-router';
import { isEmpty, isNullish } from 'remeda';
import { useLoadTemplateV2 } from 'shared/hooks/use-load-template-v2';
import { useGetConstants } from 'shared/utils/selectors';
import type { TemplateVariables } from 'types/store/app-state';
import type { DescVal } from 'types/storeTypes';
import { htmlToText, isBodyBlank } from './body-content';
import { CHANNEL_CONFIGS, type TemplateChannel } from './channels';
import {
  draftKey,
  useActiveChannelAtom,
  useSendingOptionsDraftsAtom,
  useTemplateDraftsAtom,
  useTemplateLanguageAtom,
} from './state';

export type RecommendedVariablesStatus = 'complete' | 'missing' | 'empty';

export type RecommendedVariablesStatuses = Partial<
  Record<TemplateChannel, Record<string, RecommendedVariablesStatus>>
>;

type BodyState = { status: RecommendedVariablesStatus; missing: DescVal[] };

type RecommendedVariablesStates = Partial<Record<TemplateChannel, Record<string, BodyState>>>;

const useRecommendedVariablesStates = (): RecommendedVariablesStates => {
  const { templateId } = useParams<{ templateId: string }>();
  const { template } = useLoadTemplateV2(templateId);
  const constants = useGetConstants();
  const [drafts] = useTemplateDraftsAtom();
  const [sendingOptionsDrafts] = useSendingOptionsDraftsAtom();

  // Recommended variables are a recovery-plan concept; payment plans never surfaced them.
  if (!template || template.planType === 'payment') return {};

  const planVariables = constants?.recovery_plan_template_variables;
  if (!planVariables) return {};

  const states: RecommendedVariablesStates = {};

  for (const { key: channel } of CHANNEL_CONFIGS) {
    const bodies = template.bodies[channel];
    if (isEmpty(bodies)) continue;

    const byLocale: Record<string, BodyState> = {};
    for (const body of bodies) {
      const locale = body.locale;
      const bodyDraftKey = draftKey(template.id, channel, locale);
      const content = drafts[bodyDraftKey] ?? body.body;
      const subject = sendingOptionsDrafts[bodyDraftKey]?.subject ?? body.subject ?? '';
      const missing = computeMissingRecommendedVariables({
        planVariables,
        stepType: template.stepType,
        groupedType: body.groupedType,
        channel,
        content,
        subject,
      });
      const status: RecommendedVariablesStatus = isBodyBlank(content)
        ? 'empty'
        : isEmpty(missing)
          ? 'complete'
          : 'missing';
      byLocale[locale] = { status, missing };
    }
    states[channel] = byLocale;
  }

  return states;
};

export const useMissingRecommendedVariables = (): DescVal[] => {
  const states = useRecommendedVariablesStates();
  const [activeChannel] = useActiveChannelAtom();
  const [language] = useTemplateLanguageAtom();

  const channel = activeChannel ?? (Object.keys(states)[0] as TemplateChannel | undefined);
  if (!channel || isNullish(language)) return [];

  return states[channel]?.[language]?.missing ?? [];
};

export const useRecommendedVariablesStatuses = (): RecommendedVariablesStatuses => {
  const states = useRecommendedVariablesStates();

  const statuses: RecommendedVariablesStatuses = {};
  for (const { key: channel } of CHANNEL_CONFIGS) {
    const byLocale = states[channel];
    if (!byLocale) continue;

    const localeStatuses: Record<string, RecommendedVariablesStatus> = {};
    for (const [locale, state] of Object.entries(byLocale)) {
      localeStatuses[locale] = state.status;
    }
    statuses[channel] = localeStatuses;
  }

  return statuses;
};

const computeMissingRecommendedVariables = ({
  planVariables,
  stepType,
  groupedType,
  channel,
  content,
  subject,
}: {
  planVariables: TemplateVariables;
  stepType: string;
  groupedType: string | null;
  channel: TemplateChannel;
  content: string;
  subject: string;
}): DescVal[] => {
  const recommended = selectRecommendedVariables(planVariables, stepType, groupedType);
  const text = htmlToText(content);

  return recommended.filter(({ value }) => {
    // The debtor portal link is not relevant to letters.
    if (channel === 'letter' && value === 'debtor_portal_link') return false;
    const placeholder = `{{${value}}}`;
    return !text.includes(placeholder) && !subject.includes(placeholder);
  });
};

const selectRecommendedVariables = (
  planVariables: TemplateVariables,
  stepType: string,
  groupedType: string | null,
): readonly DescVal[] => {
  if (stepType === 'credit_note') return planVariables.credit_note_warning;
  if (groupedType || stepType === 'formal_notice') return planVariables.grouped_presence_warning;
  return planVariables.presence_warning;
};
